import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, CheckCircle2, XCircle, UserCog, Mail, LogIn, KeyRound } from "lucide-react";

interface InvitationDetails {
  email: string;
  firstName: string;
  lastName: string;
  inviterName: string;
  assistantType: string;
  hasTemporaryPassword: boolean;
}

interface AcceptResponse {
  success: boolean;
  message?: string;
  nextStep?: string;
  existingUser?: boolean;
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    username: string;
  };
}

// Special error shape when the API tells us the user must log in first
interface RequiresAuthError extends Error {
  requiresAuth: true;
  email: string;
}

function isRequiresAuthError(e: unknown): e is RequiresAuthError {
  return e instanceof Error && (e as any).requiresAuth === true;
}

export default function AcceptAssistantInvitePage() {
  const [, setLocation] = useLocation();
  const [token, setToken] = useState<string | null>(null);
  const [acceptedData, setAcceptedData] = useState<AcceptResponse | null>(null);
  const [tempPassword, setTempPassword] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    setToken(tokenParam);
  }, []);

  const { data: invitation, isLoading: loadingInvitation, error: invitationError } = useQuery<InvitationDetails>({
    queryKey: ['/api/assistants/invitations/accept', token],
    queryFn: async () => {
      const response = await fetch(`/api/assistants/invitations/accept/${token}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch invitation');
      }
      return response.json();
    },
    enabled: !!token,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, any> = {
        firstName: invitation?.firstName,
        lastName: invitation?.lastName,
      };
      if (invitation?.hasTemporaryPassword) {
        body.tempPassword = tempPassword;
      }
      const response = await fetch(`/api/assistants/invitations/accept/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        if (data.requiresAuth) {
          const err = new Error(
            data.error || "Please log in with your existing account to accept this invitation."
          ) as RequiresAuthError;
          (err as any).requiresAuth = true;
          (err as any).email = data.email || invitation?.email || "";
          throw err;
        }
        throw new Error(data.message || data.error || 'Failed to accept invitation');
      }
      return data as AcceptResponse;
    },
    onSuccess: (data) => {
      if (data.nextStep === 'password-reset') {
        // New-style invite: session was created by the server.
        // Invalidate the cached /api/auth/user result (was 401 before accept) so
        // the reset-password page sees the fresh authenticated session immediately.
        queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
        setLocation('/reset-password');
        return;
      }
      setAcceptedData(data);
    },
  });

  const handleAccept = () => {
    acceptMutation.mutate();
  };

  const handleGoToLogin = () => {
    setLocation('/');
  };

  const handleLoginToAccept = () => {
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/?returnTo=${returnUrl}`;
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-error">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" data-testid="icon-error" />
            </div>
            <CardTitle data-testid="text-error-title">Invalid Invitation</CardTitle>
            <CardDescription data-testid="text-error-description">
              No invitation token provided
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleGoToLogin} className="w-full" data-testid="button-go-home">
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loadingInvitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-loading">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-indigo-600 dark:text-indigo-400 animate-spin" data-testid="icon-loading" />
            </div>
            <CardTitle data-testid="text-loading-title">Loading Invitation</CardTitle>
            <CardDescription data-testid="text-loading-description">
              Please wait...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (invitationError || !invitation) {
    const errorMessage = invitationError instanceof Error
      ? invitationError.message
      : 'Invitation not found or has expired';

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-error">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" data-testid="icon-error" />
            </div>
            <CardTitle data-testid="text-error-title">Invitation Error</CardTitle>
            <CardDescription data-testid="text-error-description">
              {errorMessage}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleGoToLogin} className="w-full" data-testid="button-go-home">
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Legacy success screen (existing users and old-style invites that returned success without nextStep)
  if (acceptedData) {
    const isExistingUser = !!acceptedData.existingUser;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-success">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" data-testid="icon-success" />
            </div>
            <CardTitle data-testid="text-success-title">Welcome Aboard!</CardTitle>
            <CardDescription data-testid="text-success-description">
              {isExistingUser
                ? "You've been added as an assistant successfully"
                : "Your assistant account has been created successfully"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isExistingUser ? (
              <Alert className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800" data-testid="alert-existing-user">
                <AlertDescription className="text-sm text-green-800 dark:text-green-200">
                  Your existing account has been linked as an assistant. You can now log in with your existing credentials to access the shared content.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="bg-indigo-50 border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-800" data-testid="alert-credentials">
                <Mail className="h-4 w-4" />
                <AlertDescription className="space-y-1">
                  <p className="font-semibold text-sm" data-testid="text-credentials-header">
                    Check your email
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-testid="text-credentials-info">
                    Your login credentials have been sent to <strong>{acceptedData.user?.email}</strong>. Use them to sign in and set a permanent password.
                  </p>
                </AlertDescription>
              </Alert>
            )}
            <Button onClick={handleGoToLogin} className="w-full" data-testid="button-go-login">
              {isExistingUser ? "Go to Dashboard" : "Go to Login"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // "Requires auth" state — existing account found, user needs to log in first
  if (acceptMutation.isError && isRequiresAuthError(acceptMutation.error)) {
    const authErr = acceptMutation.error;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-requires-auth">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
              <LogIn className="w-6 h-6 text-amber-600 dark:text-amber-400" data-testid="icon-requires-auth" />
            </div>
            <CardTitle data-testid="text-requires-auth-title">Sign In Required</CardTitle>
            <CardDescription data-testid="text-requires-auth-description">
              An account already exists for <strong>{authErr.email}</strong>. You must be signed in as that account to accept this invitation.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
              <AlertDescription className="text-sm text-amber-800 dark:text-amber-200">
                After signing in, return to this page and click <strong>Accept Invitation</strong> again.
              </AlertDescription>
            </Alert>
            <Button onClick={handleLoginToAccept} className="w-full" data-testid="button-login-to-accept">
              <LogIn className="w-4 h-4 mr-2" />
              Sign In to Accept
            </Button>
            <Button variant="outline" onClick={handleGoToLogin} className="w-full" data-testid="button-cancel">
              Cancel
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main invitation card
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <Card className="max-w-lg w-full" data-testid="card-invitation">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center">
            <UserCog className="w-8 h-8 text-indigo-600 dark:text-indigo-400" data-testid="icon-assistant" />
          </div>
          <CardTitle className="text-2xl" data-testid="text-title">Assistant Invitation</CardTitle>
          <CardDescription data-testid="text-description">
            <strong>{invitation.inviterName}</strong> has invited you to become their assistant
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border p-3 space-y-2" data-testid="assistant-invitation-access-notice">
            <p className="text-sm font-medium">Individual shares only</p>
            <p className="text-xs text-muted-foreground">This relationship does not provide blanket access to Notes/Notepad, Goals, Calendar, Tasks, Habits, or Tracker-X. You can access only records that are shared with you individually.</p>
          </div>

          <div className="bg-muted/50 p-4 rounded-lg space-y-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Email</Label>
              <Input
                value={invitation.email}
                readOnly
                className="bg-muted/50 cursor-not-allowed"
                data-testid="input-invitation-email"
              />
            </div>

            {invitation.hasTemporaryPassword && (
              <div className="space-y-1">
                <Label htmlFor="temp-password" className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <KeyRound className="w-3 h-3" />
                  Temporary password (from your invitation email)
                </Label>
                <Input
                  id="temp-password"
                  type="password"
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                  placeholder="Enter the password from your email"
                  autoComplete="current-password"
                  data-testid="input-temp-password"
                />
              </div>
            )}

            {!invitation.hasTemporaryPassword && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Name:</span>
                <span className="font-medium" data-testid="text-name">
                  {invitation.firstName} {invitation.lastName}
                </span>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleGoToLogin}
              className="flex-1"
              data-testid="button-decline"
            >
              Decline
            </Button>
            <Button
              onClick={handleAccept}
              disabled={acceptMutation.isPending || (invitation.hasTemporaryPassword && !tempPassword)}
              className="flex-1"
              data-testid="button-accept"
            >
              {acceptMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Setting up…
                </>
              ) : (
                invitation.hasTemporaryPassword ? "Set Up Account" : "Accept Invitation"
              )}
            </Button>
          </div>

          {acceptMutation.isError && !isRequiresAuthError(acceptMutation.error) && (
            <p className="text-sm text-red-600 dark:text-red-400 text-center" data-testid="text-error">
              {acceptMutation.error instanceof Error ? acceptMutation.error.message : 'Failed to accept invitation'}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
